import React, { useMemo } from 'react';
import { Box, Link } from '@chakra-ui/react';
import { AppSkeleton } from '@/components/ui/skeleton';
import { ErrorBoundary } from 'react-error-boundary';
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CitationOut, DocumentWithDownloadUrl as Document, SourceOut } from '@/client';
import {
	CitationPreviewEntry,
	getCitationPreviewEntries,
	getContractCitationIndices,
	getExtraDocumentCount,
	getRecordCitationPillLayout,
	getSourceKey,
	groupCitationPreviewEntries,
	LOADING_CITATION_HREF,
} from '../citationUtils.ts';
import { SHOW_CITATION_PILLS_FEATURE_FLAG } from '@/components/dashbaord/assistant/featureFlags.ts';
import CitationPreviewPopover from './CitationPreviewPopover.tsx';
import SourceFullscreenPreview from './SourceFullscreenPreview.tsx';
import MarkdownFallback from './MarkdownFallback.tsx';
import CitationSourcePill from './CitationSourcePill.tsx';

interface ChatResponseTextProps {
	responseText: string;
	parseResponseTextWithLinks: (text: string) => string;
	/** Citation contract: one source per document, plus per-marker page locators (reading order). */
	sources?: SourceOut[];
	citations?: CitationOut[];
	documents?: Document[];
	isLoading: boolean;
	/** true → labeled source pills; false → compact, clickable [n] markers. Defaults to the flag. */
	showCitationPills?: boolean;
	onCitationClick: (sourceIndex: number) => void;
}

const transformMarkdownUrl = (url: string): string =>
	url.toLowerCase().startsWith('tel:') ? url : defaultUrlTransform(url);

const SOURCE_PILL_MAX_WIDTH = '128px';
const SOURCE_PILL_VERTICAL_ALIGN = '0.08em';

/**
 * Placeholder shown at a citation marker while the answer is still streaming (no sources/citations
 * contract yet). Sized like a real source pill so the swap at completion barely reflows; contentless,
 * so it can't be wrong about which source it points at. Decorative — hidden from the a11y tree.
 */
const SourceSkeletonPill = (
	<AppSkeleton
		aria-hidden="true"
		display="inline-flex"
		verticalAlign={SOURCE_PILL_VERTICAL_ALIGN}
		w="44px"
		h="20px"
		mx={0.5}
		borderRadius="full"
	/>
);

/**
 * Renders assistant response text as Markdown with a safe fallback.
 * The fallback shows plain text if Markdown rendering fails.
 */
const ChatResponseText: React.FC<ChatResponseTextProps> = ({
	responseText,
	parseResponseTextWithLinks,
	sources,
	citations,
	documents = [],
	isLoading,
	showCitationPills = SHOW_CITATION_PILLS_FEATURE_FLAG,
	onCitationClick,
}) => {
	const [fullscreenSourceRequest, setFullscreenSourceRequest] = React.useState<{
		document: Document;
		page: number;
		requestId: number;
	} | null>(null);

	const openFullscreenPage = React.useCallback(
		(entry: CitationPreviewEntry, page: number) => {
			if (!entry.document?.download_url) {
				onCitationClick(entry.citation.ordinal - 1);
				return;
			}
			setFullscreenSourceRequest((current) => ({
				document: entry.document as Document,
				page,
				requestId: (current?.requestId ?? 0) + 1,
			}));
		},
		[onCitationClick],
	);

	// Memoized so a citation-pill click (which re-renders this subtree's parent) doesn't rebuild the
	// markdown component map and momentarily reflow the pills. Recomputes only when the citation
	// inputs actually change — which requires onCitationClick to be a stable handler (see ChatContainer).
	const components = useMemo<Components>(() => {
		// One pill renderer for both paths: stable source number + optional extra document count.
		const renderPill = (entries: CitationPreviewEntry[]) => {
			const groups = groupCitationPreviewEntries(entries);
			const { visibleItems: visibleGroups, hiddenCount: hiddenGroupCount } = getRecordCitationPillLayout(groups);
			const hiddenGroup = groups[visibleGroups.length];
			const openSource = (sourceOrdinal: number) => (event: React.MouseEvent | React.KeyboardEvent) => {
				event.preventDefault();
				event.stopPropagation();
				onCitationClick(sourceOrdinal - 1);
			};
			return (
				<Box
					as="span"
					display="inline-flex"
					alignItems="center"
					gap={0.5}
					verticalAlign={SOURCE_PILL_VERTICAL_ALIGN}
					maxW={SOURCE_PILL_MAX_WIDTH}
					mx={0.5}
					overflow="visible"
					whiteSpace="nowrap"
				>
					{visibleGroups.map((group) => {
						const groupSourceKey = getSourceKey(group.source);
						const openGroupSource = openSource(group.citation.ordinal);
						return (
							<CitationPreviewPopover
								key={groupSourceKey}
								entries={entries}
								initialSourceKey={groupSourceKey}
								onOpenSource={(nextSourceIndex) => onCitationClick(nextSourceIndex)}
								onOpenFullscreenPage={openFullscreenPage}
							>
								<CitationSourcePill label={group.citation.ordinal} onOpen={openGroupSource} />
							</CitationPreviewPopover>
						);
					})}
					{hiddenGroupCount > 0 && hiddenGroup && (
						<CitationPreviewPopover
							entries={entries}
							initialSourceKey={getSourceKey(hiddenGroup.source)}
							onOpenSource={(nextSourceIndex) => onCitationClick(nextSourceIndex)}
							onOpenFullscreenPage={openFullscreenPage}
						>
							<CitationSourcePill
								label={`+${hiddenGroupCount}`}
								onOpen={openSource(hiddenGroup.citation.ordinal)}
							/>
						</CitationPreviewPopover>
					)}
				</Box>
			);
		};

		// Compact alternative to the verbose pill: a clickable `[n]` reference (state-of-the-art style).
		// The filename/page lives in the popover; clicking still opens the source in the drawer.
		const renderCompactCitation = (entries: CitationPreviewEntry[]) => {
			const firstEntry = entries[0];
			const sourceIndex = firstEntry.citation.ordinal - 1;
			const extraDocumentCount = getExtraDocumentCount(entries);
			const openSource = (event: React.MouseEvent | React.KeyboardEvent) => {
				event.preventDefault();
				event.stopPropagation();
				onCitationClick(sourceIndex);
			};
			return (
				<CitationPreviewPopover
					entries={entries}
					onOpenSource={(nextSourceIndex) => onCitationClick(nextSourceIndex)}
					onOpenFullscreenPage={openFullscreenPage}
				>
					<Box
						as="span"
						role="button"
						tabIndex={0}
						mx="1px"
						color="brand.fg"
						fontWeight="medium"
						whiteSpace="nowrap"
						cursor="pointer"
						borderRadius="sm"
						_hover={{ textDecoration: 'underline' }}
						_focusVisible={{
							outline: '2px solid',
							outlineColor: 'brand.fg',
							outlineOffset: '2px',
						}}
						onClick={openSource}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								openSource(event);
							}
						}}
					>
						[{firstEntry.citation.ordinal}]{extraDocumentCount > 0 ? `+${extraDocumentCount}` : ''}
					</Box>
				</CitationPreviewPopover>
			);
		};

		return {
			a: ({ children, ...props }) => {
				// While streaming, a citation marker is tagged with #cite-loading (no source data exists
				// yet) — render a neutral skeleton in its place until the contract arrives.
				if (isLoading && props.href === LOADING_CITATION_HREF) return SourceSkeletonPill;

				// The marker's anchor carries its reading-order index (#cite-N). Resolve it to
				// (document source, page) via citations[]/sources[] — pure lookup, no prose parsing.
				const citationIndices = isLoading ? [] : getContractCitationIndices(props.href);
				if (citationIndices.length > 0 && citations && sources) {
					const entries = getCitationPreviewEntries(citationIndices, citations, sources, documents);
					if (entries.length > 0) {
						return showCitationPills ? renderPill(entries) : renderCompactCitation(entries);
					}
				}

				// For regular links, render normally
				return <Link {...props}>{children}</Link>;
			},
		};
	}, [isLoading, sources, citations, documents, showCitationPills, onCitationClick, openFullscreenPage]);

	// Memoized for the same reason: keep the parsed markdown string stable across re-renders that
	// don't change the text, so ReactMarkdown doesn't reprocess the whole answer on every pill click.
	const markdown = useMemo(
		() => parseResponseTextWithLinks(responseText),
		[parseResponseTextWithLinks, responseText],
	);

	// Memoize the *element*, not just its inputs: ChatResponseText re-renders when a pill click bumps
	// the parent's drawer state, and returning the same element reference makes React skip the whole
	// ReactMarkdown subtree (a worthwhile re-render saving on click and during streaming siblings).
	// TODO: this did NOT fix the pill label briefly un-truncating on click — ruling out a markdown
	// re-render as the cause. Remaining suspects are side effects of the drawer opening on the same
	// click: its body scroll-lock momentarily shifting layout width, or focus moving to the pill.
	// Investigate there (or the pill's own focus/active styling) rather than the rendering path.
	const markdownContent = useMemo(
		() => (
			<ReactMarkdown
				children={markdown}
				remarkPlugins={[remarkGfm]}
				components={components}
				// Raw HTML is intentionally disabled for assistant text. If assistant-authored HTML
				// becomes a product feature, re-enable it only with a strict rehype-sanitize schema.
				urlTransform={transformMarkdownUrl}
			/>
		),
		[markdown, components],
	);

	if (!responseText) return null;

	return (
		<ErrorBoundary fallback={<MarkdownFallback responseText={responseText} />}>
			<Box
				fontSize={{ base: 'sm', md: '1rem' }}
				lineHeight={1.6}
				css={{
					'& p': { marginBottom: '0.85rem' },
					'& p:last-child': { marginBottom: 0 },
					'& ul, & ol': { paddingLeft: '1.25rem', marginBottom: '0.85rem' },
					'& ul': { listStyleType: 'disc' },
					'& ol': { listStyleType: 'decimal' },
					'& li': { marginBottom: '0.3rem' },
					'& li:last-child': { marginBottom: 0 },
					'& li > ul, & li > ol': { marginTop: '0.3rem', marginBottom: 0 },
					'& h1, & h2, & h3, & h4': {
						fontWeight: 600,
						lineHeight: 1.3,
						marginTop: '1.25rem',
						marginBottom: '0.5rem',
					},
					'& h1': { fontSize: '1.25rem' },
					'& h2': { fontSize: '1.15rem' },
					'& h3, & h4': { fontSize: '1.05rem' },
					'& :is(h1, h2, h3, h4):first-child': { marginTop: 0 },
					'& strong': { fontWeight: 600 },
					'& a': { color: 'brand.fg', textDecoration: 'underline' },
					'& code': {
						fontFamily: 'mono',
						fontSize: '0.875em',
						bg: 'bg.muted',
						px: '0.3em',
						py: '0.1em',
						borderRadius: '4px',
					},
					'& pre': {
						bg: 'bg.muted',
						p: '0.75rem',
						borderRadius: '8px',
						overflowX: 'auto',
						marginBottom: '0.85rem',
					},
					'& pre code': { bg: 'transparent', p: 0, fontSize: '0.85em' },
					'& blockquote': {
						borderLeftWidth: '3px',
						borderColor: 'border.default',
						paddingLeft: '0.75rem',
						color: 'fg.muted',
						marginBottom: '0.85rem',
					},
					'& hr': { borderColor: 'border.default', marginBlock: '1rem' },
				}}
			>
				{markdownContent}
				{fullscreenSourceRequest && (
					<SourceFullscreenPreview
						key={fullscreenSourceRequest.requestId}
						document={fullscreenSourceRequest.document}
						page={fullscreenSourceRequest.page}
						onClose={() => setFullscreenSourceRequest(null)}
					/>
				)}
			</Box>
		</ErrorBoundary>
	);
};

export default ChatResponseText;
